import { PropsWithChildren, useEffect } from "react"
import {
  backButton,
  closingBehavior,
  init as initSDK,
  initData,
  isRequestingWriteAccess,
  mainButton,
  miniApp,
  requestWriteAccess,
  retrieveLaunchParams,
  setDebug,
  setMiniAppBackgroundColor,
  setMiniAppBottomBarColor,
  setMiniAppHeaderColor,
  swipeBehavior,
  themeParams,
  useSignal,
  viewport,
} from "@telegram-apps/sdk-react"

export const TMAProvider = ({ children }: PropsWithChildren) => {
  const debug = import.meta.env.NODE_ENV === "development"

  useEffect(() => {
    initSDK()

    initData.restore()

    if (!miniApp.isMounted() && miniApp.mountSync.isAvailable())
      miniApp.mountSync()

    if (!closingBehavior.isMounted() && closingBehavior.mount.isAvailable())
      closingBehavior.mount()

    if (
      !viewport.isMounted() &&
      !viewport.isMounting() &&
      viewport.mount.isAvailable()
    )
      viewport.mount()

    if (!swipeBehavior.isMounted() && swipeBehavior.mount.isAvailable())
      swipeBehavior.mount()

    if (
      backButton.isSupported() &&
      !backButton.isMounted() &&
      backButton.mount.isAvailable()
    )
      backButton.mount()

    if (!mainButton.isMounted() && mainButton.mount.isAvailable())
      mainButton.mount()
  }, [])

  useEffect(() => {
    setDebug(debug)
    if (debug) void import("eruda").then((lib) => lib.default.init())
  }, [debug])

  const isThemeParamsMounted = useSignal(themeParams.isMounted)
  useEffect(() => {
    if (isThemeParamsMounted && !themeParams.isCssVarsBound())
      themeParams.bindCssVars()
  }, [isThemeParamsMounted])

  const isMiniAppMounted = useSignal(miniApp.isMounted)
  useEffect(() => {
    if (isMiniAppMounted && !miniApp.isCssVarsBound()) miniApp.bindCssVars()
  }, [isMiniAppMounted])

  const isClosingBehaviorMounted = useSignal(closingBehavior.isMounted)
  useEffect(() => {
    if (isClosingBehaviorMounted && !closingBehavior.isConfirmationEnabled())
      closingBehavior.enableConfirmation()
  }, [isClosingBehaviorMounted])

  useEffect(() => {
    if (!isRequestingWriteAccess()) void requestWriteAccess()
  }, [])

  const isViewportMounted = useSignal(viewport.isMounted)

  useEffect(() => {
    const initViewport = async () => {
      if (!viewport.isCssVarsBound()) viewport.bindCssVars()

      if (!viewport.isExpanded() && viewport.expand.isAvailable())
        viewport.expand()

      const platform = String(retrieveLaunchParams().tgWebAppPlatform)
      const wantsFullscreen =
        !viewport.isFullscreen() &&
        !viewport.changeFullscreenPromise &&
        viewport.requestFullscreen.isAvailable() &&
        ["ios", "android", "android_x"].includes(platform)

      if (wantsFullscreen) await viewport.requestFullscreen()
    }

    if (isViewportMounted) void initViewport()
  }, [isViewportMounted])

  const isSwipeBehaviorMounted = useSignal(swipeBehavior.isMounted)
  useEffect(() => {
    if (
      isSwipeBehaviorMounted &&
      swipeBehavior.isVerticalEnabled() &&
      swipeBehavior.disableVertical.isAvailable()
    )
      swipeBehavior.disableVertical()
  }, [isSwipeBehaviorMounted])

  const isTmaDark = useSignal(themeParams.isDark)
  const isSystemDark = globalThis.matchMedia(
    "(prefers-color-scheme: dark)"
  ).matches

  useEffect(() => {
    const setTheme = () => {
      const isDark = isThemeParamsMounted ? isTmaDark : isSystemDark

      const color = isDark ? "#171717" : "#F4F4F4"

      if (setMiniAppBackgroundColor.isAvailable())
        setMiniAppBackgroundColor(color)
      if (setMiniAppHeaderColor.isAvailable()) setMiniAppHeaderColor(color)
      if (setMiniAppBottomBarColor.isAvailable())
        setMiniAppBottomBarColor(color)

      if (isDark) document.documentElement.classList.add("dark")
      else document.documentElement.classList.remove("dark")
    }

    setTheme()

    const mediaQuery = globalThis.matchMedia("(prefers-color-scheme: dark)")
    mediaQuery.addEventListener("change", setTheme)
    return () => mediaQuery.removeEventListener("change", setTheme)
  }, [isThemeParamsMounted, isTmaDark, isSystemDark])

  useEffect(() => {
    const phCookie = document.cookie
      .split(";")
      .find((c) => c.trim().startsWith("ph_phc_"))
    if (phCookie) {
      const paths = ["/", "/ru", "/en", "", window.location.pathname]
      const domains = [
        window.location.hostname,
        "." + window.location.hostname,
        "",
        "funfiesta.games",
        ".funfiesta.games",
      ]
      const sameSite = ["Lax", "Strict", "None"]
      const flags = [["secure", "HttpOnly"], ["secure"], ["HttpOnly"], []]

      paths.forEach((path) => {
        domains.forEach((domain) => {
          sameSite.forEach((same) => {
            flags.forEach((flag) => {
              const flagString = flag.length > 0 ? `; ${flag.join("; ")}` : ""
              document.cookie = `${phCookie.split("=")[0]}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}${flagString}; SameSite=${same}`
            })
          })
        })
      })
    }
  }, [])

  return (
    <div className="safe-area">
      <div className="page-container flex max-w-full flex-col">{children}</div>
    </div>
  )
}
